import { BellRing, Check, Radio, Webhook } from "lucide-react";
import {
  gridTrades,
  linePath,
  smoothPoints,
  type Pt,
} from "features/onboarding/art/chart";
import {
  Art,
  Card,
  Chip,
  Head,
  Line,
  Marker,
} from "features/onboarding/art/live";
import {
  TXT,
  cornerChip,
  count,
  draw,
  fade,
  follow,
  font,
  pop,
  r2,
  reach,
  ride,
  ring,
  rise,
  say,
  soft,
  tone,
  useArtId,
  type Box,
  type Scene,
} from "features/onboarding/art/kit";
import useArtLabels from "features/onboarding/art/labels";

/*
 * Compact versions of the cornix.io feature illustrations, for cards. Same
 * drawing kit and motion, a 320×160 canvas and little text, so they stay
 * legible from a phone card up to a desktop tile.
 */

const W = 320;
const H = 160;

type ThumbProps = { play?: boolean; fill?: boolean };

const ChartCard = ({ id, b }: { id: string; b: Box }) => (
  <g data-in="0">
    <Card id={id} x={b.x} y={b.y} w={b.w} h={b.h} r={14} />
  </g>
);

const mapPts = (b: Box, keys: Pt[], steps = 10): Pt[] =>
  smoothPoints(
    keys.map(([u, v]): Pt => [b.x + u * b.w, b.y + v * b.h]),
    steps
  ).map(([x, y]): Pt => [r2(x), r2(y)]);

/* ------------------------------------------------------------------------ */
/* Signals: a posted signal becomes orders, then the targets fill.          */
/* ------------------------------------------------------------------------ */

export function SignalsThumb({ play, fill }: ThumbProps) {
  const L = useArtLabels();
  const id = useArtId("thumb-signals");
  const msg: Box = { x: 10, y: 22, w: 112, h: 118 };
  const chart: Box = { x: 136, y: 12, w: 174, h: 136 };
  const band: Box = { x: 148, y: 30, w: 122, h: 104 };
  const ENTRY = 0.72;
  const TPS = [0.42, 0.24, 0.08];
  const SL = 0.93;
  const pts = mapPts(band, [
    [0, 0.5],
    [0.38, 0.78],
    [0.64, 0.42],
    [1, 0.08],
  ]);
  const Y = (u: number) => r2(band.y + u * band.h);
  const buy = reach(pts, Y(ENTRY), "down")!;
  const hits = TPS.map(
    (u) => reach(pts, Y(u), "up", buy.i) ?? { f: 1, x: 0, y: 0, i: 0 }
  );
  const end = pts[pts.length - 1];
  const START = 1.3;
  const DRAW = 2.8;

  const scene: Scene = (tl, $) => {
    rise(tl, $("msg"), 0.1, 10, 0.6);
    tl.fromTo(
      $("packet"),
      { attr: { cx: msg.x + msg.w }, opacity: 1 },
      { attr: { cx: chart.x }, duration: 0.45, ease: "power2.in" },
      0.7
    );
    tl.to($("packet"), { opacity: 0, duration: 0.15 }, 1.15);
    rise(tl, $("levels"), 1.1, 0, 0.5);
    ride(tl, $("price"), $("head")[0], pts, START, DRAW);
    pop(tl, $("buy"), START + buy.f * DRAW, 5);
    hits.forEach((h, i) => rise(tl, $(`hit${i}`), START + h.f * DRAW, 0, 0.3));
    const rest = START + DRAW + 0.6;
    const out = rest + 2.6;
    fade(
      tl,
      [
        ...$("msg"),
        ...$("levels"),
        ...$("price"),
        ...$("head"),
        ...$("buy"),
        ...$("hit*"),
      ],
      out
    );
    tl.to({}, { duration: 0.5 }, out + 0.5);
    return rest;
  };

  const tagX = band.x + band.w + 6;
  const tagW = chart.x + chart.w - 8 - tagX;
  const level = (u: number, t: "up" | "down" | "accent", dash: string) => (
    <g key={`${t}${u}`}>
      <line
        x1={band.x}
        x2={band.x + band.w}
        y1={Y(u)}
        y2={Y(u)}
        stroke={tone(t)}
        strokeOpacity={0.7}
        strokeWidth={1.2}
        strokeDasharray={dash}
      />
      <rect
        x={tagX}
        y={Y(u) - 7}
        width={tagW}
        height={14}
        rx={5}
        fill={soft(t)}
      />
    </g>
  );

  return (
    <Art id={id} w={W} h={H} scene={scene} play={play} fill={fill}>
      <g data-k="msg">
        <Card id={id} x={msg.x} y={msg.y} w={msg.w} h={msg.h} r={14}>
          <circle cx={msg.x + 20} cy={msg.y + 20} r={11} fill={tone("ink")} />
          <Radio
            x={msg.x + 13.5}
            y={msg.y + 13.5}
            width={13}
            height={13}
            color="var(--viz-card)"
            strokeWidth={2.2}
          />
          <rect
            x={msg.x + 38}
            y={msg.y + 14}
            width={58}
            height={6}
            rx={3}
            fill="var(--viz-ink-soft)"
          />
          <rect
            x={msg.x + 38}
            y={msg.y + 24}
            width={36}
            height={5}
            rx={2.5}
            fill="var(--viz-rule-soft)"
          />
          <rect
            x={msg.x + 10}
            y={msg.y + 42}
            width={msg.w - 20}
            height={msg.h - 52}
            rx={10}
            fill="var(--viz-accent-faint)"
            stroke="var(--viz-rule-soft)"
          />
          <text
            x={msg.x + 18}
            y={msg.y + 60}
            className={`${TXT.ink} ${TXT.num}`}
            style={font(12, 600)}
          >
            BTC
          </text>
          <rect
            x={msg.x + 46}
            y={msg.y + 51}
            width={34}
            height={13}
            rx={6.5}
            fill={soft("up")}
            stroke={tone("up")}
            strokeOpacity={0.35}
          />
          <path
            d={`M${msg.x + 57},${msg.y + 60} l5,-5 l5,5`}
            fill="none"
            stroke={tone("up")}
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {[0, 1, 2].map((i) => (
            <g key={i}>
              <rect
                x={msg.x + 18}
                y={msg.y + 72 + i * 14}
                width={16}
                height={5}
                rx={2.5}
                fill={soft(i === 2 ? "down" : i === 1 ? "up" : "accent")}
              />
              <rect
                x={msg.x + 40}
                y={msg.y + 72 + i * 14}
                width={i === 1 ? 54 : 42}
                height={5}
                rx={2.5}
                fill="var(--viz-ink-soft)"
              />
            </g>
          ))}
        </Card>
      </g>
      <line
        data-in="0.1"
        x1={msg.x + msg.w}
        x2={chart.x}
        y1={msg.y + msg.h / 2}
        y2={msg.y + msg.h / 2}
        stroke="var(--viz-accent-line)"
        strokeWidth={2}
        strokeDasharray="3 4"
      />
      <circle
        data-k="packet"
        cx={chart.x}
        cy={msg.y + msg.h / 2}
        r={3.5}
        fill={tone("accent")}
        opacity={0}
      />
      <ChartCard id={id} b={chart} />
      <g data-k="levels">
        <rect
          x={band.x}
          y={Y(ENTRY) - 5}
          width={band.w}
          height={10}
          fill={soft("accent")}
          opacity={0.8}
        />
        {level(ENTRY, "accent", "0")}
        {TPS.map((u) => level(u, "up", "4 3"))}
        {level(SL, "down", "4 3")}
      </g>
      {TPS.map((u, i) => (
        <g key={u} data-k={`hit${i}`}>
          <rect
            x={tagX}
            y={Y(u) - 7}
            width={tagW}
            height={14}
            rx={5}
            fill={tone("up")}
          />
          <Check
            x={tagX + tagW / 2 - 5}
            y={Y(u) - 5}
            width={10}
            height={10}
            color="var(--viz-on-solid)"
            strokeWidth={3}
          />
        </g>
      ))}
      <Line k="price" d={linePath(pts)} w={2} />
      <Marker k="buy" x={buy.x} y={buy.y} t="up" r={5} />
      <Head k="head" x={end[0]} y={end[1]} t="up" />
      <text
        data-in="0.2"
        x={chart.x + 12}
        y={chart.y + 17}
        className={TXT.label}
        style={font(11, 500)}
      >
        {L.following}
      </text>
    </Art>
  );
}

/* ------------------------------------------------------------------------ */
/* Grid: buys below, sells one level above, as the price moves sideways.    */
/* ------------------------------------------------------------------------ */

const gridWave = (u: number) =>
  0.5 + 0.34 * Math.sin(11.3 * u) * (0.8 + 0.2 * Math.sin(4 * u + 0.5));

export function GridThumb({ play, fill }: ThumbProps) {
  const L = useArtLabels();
  const id = useArtId("thumb-grid");
  const chart: Box = { x: 10, y: 10, w: 300, h: 140 };
  const band: Box = { x: 30, y: 30, w: 186, h: 104 };
  const LEVELS = 5;
  const PROFIT = 7.1;
  const levels = Array.from(
    { length: LEVELS },
    (_, i) => band.y + ((i + 1) * band.h) / (LEVELS + 1)
  );
  const pts: Pt[] = Array.from({ length: 121 }, (_, i) => {
    const u = i / 120;
    return [band.x + u * band.w, r2(band.y + gridWave(u) * band.h)];
  });
  const trades = gridTrades(pts, levels);
  const sells = trades.filter((t) => t.side === "sell").length;
  const end = pts[pts.length - 1];
  const START = 0.4;
  const DRAW = 4.2;

  const scene: Scene = (tl, $) => {
    ride(tl, $("price"), $("head")[0], pts, START, DRAW);
    let s = 0;
    trades.forEach((t, i) => {
      const time = START + t.f * DRAW;
      pop(tl, $(`dot${i}`), time, 6.5);
      rise(tl, $(`dot-text${i}`), time + 0.05, 0, 0.3);
      if (t.side === "sell") s++;
      say(tl, $("profit")[0], `+$${(s * PROFIT).toFixed(2)}`, time, "+$0.00");
      say(tl, $("trades")[0], String(i + 1), time, "0");
    });
    const rest = START + DRAW + 0.6;
    const out = rest + 2.6;
    fade(tl, [...$("price"), ...$("head"), ...$("dot*")], out);
    tl.to({}, { duration: 0.5 }, out + 0.5);
    return rest;
  };

  const statX = band.x + band.w + 22;
  return (
    <Art id={id} w={W} h={H} scene={scene} play={play} fill={fill}>
      <ChartCard id={id} b={chart} />
      <g data-in="0.1">
        <rect
          x={band.x - 8}
          y={band.y}
          width={band.w + 16}
          height={band.h}
          rx={6}
          fill="var(--viz-accent-faint)"
          stroke="var(--viz-accent-line)"
          strokeDasharray="4 4"
        />
        {levels.map((y) => (
          <line
            key={y}
            x1={band.x - 8}
            x2={band.x + band.w + 8}
            y1={y}
            y2={y}
            stroke="var(--viz-rule)"
          />
        ))}
      </g>
      <Line k="price" d={linePath(pts)} w={2} />
      {trades.map((t, i) => {
        const isBuy = t.side === "buy";
        return (
          <g key={i}>
            <circle
              data-k={`dot${i}`}
              cx={t.x}
              cy={t.y}
              r={6.5}
              fill={tone(isBuy ? "up" : "accent")}
              stroke="var(--viz-card)"
              strokeWidth={1.5}
            />
            <text
              data-k={`dot-text${i}`}
              x={t.x}
              y={t.y + 3}
              textAnchor="middle"
              fill="var(--viz-on-solid)"
              style={{ ...font(8.5, 600), pointerEvents: "none" }}
            >
              {(isBuy ? L.buy : L.sell).charAt(0).toUpperCase()}
            </text>
          </g>
        );
      })}
      <Head k="head" x={end[0]} y={end[1]} />
      <g data-in="0.2">
        <text
          x={statX}
          y={band.y + 12}
          className={TXT.label}
          style={font(11, 500)}
        >
          {L.profit}
        </text>
        <text
          data-k="profit"
          x={statX}
          y={band.y + 32}
          fill={tone("up")}
          className={TXT.num}
          style={font(15, 600)}
        >
          {`+$${(sells * PROFIT).toFixed(2)}`}
        </text>
        <line
          x1={statX}
          x2={chart.x + chart.w - 14}
          y1={band.y + 48}
          y2={band.y + 48}
          stroke="var(--viz-rule-soft)"
        />
        <text
          x={statX}
          y={band.y + 68}
          className={TXT.label}
          style={font(11, 500)}
        >
          {L.trades}
        </text>
        <text
          data-k="trades"
          x={statX}
          y={band.y + 88}
          className={`${TXT.ink} ${TXT.num}`}
          style={font(15, 600)}
        >
          {String(trades.length)}
        </text>
      </g>
    </Art>
  );
}

/* ------------------------------------------------------------------------ */
/* TradingView: an alert fires on a crossover and the bot places the order. */
/* ------------------------------------------------------------------------ */

export function TradingViewThumb({ play, fill }: ThumbProps) {
  const L = useArtLabels();
  const id = useArtId("thumb-tv");
  const chart: Box = { x: 10, y: 12, w: 196, h: 136 };
  const plot: Box = { x: 24, y: 48, w: 168, h: 86 };
  const bot: Box = { x: 220, y: 36, w: 90, h: 92 };
  const longW = Math.max(46, Math.round(L.long.length * 6.4 + 18));
  // Ends below the webhook wire, so the head never sits on it.
  const price = mapPts(plot, [
    [0, 0.3],
    [0.38, 0.8],
    [0.76, 0.44],
    [0.97, 0.36],
  ]);
  const ma = mapPts(plot, [
    [0, 0.26],
    [0.3, 0.46],
    [0.55, 0.56],
    [0.8, 0.5],
    [1, 0.4],
  ]);
  const maAt = (x: number) => {
    const i = Math.max(
      1,
      ma.findIndex(([mx]) => mx >= x)
    );
    const [ax, ay] = ma[i - 1];
    const [bx, by] = ma[i];
    return ay + ((by - ay) * (x - ax)) / (bx - ax || 1);
  };
  const low = price.reduce((b, p, i) => (p[1] > price[b][1] ? i : b), 0);
  const crossI = Math.max(
    1,
    price.findIndex(([x, y], i) => i > low && y < maAt(x))
  );
  const alert = price[crossI];
  const fAlert = follow(price).fractions[crossI];
  const end = price[price.length - 1];
  // From the bell's right side, over the chart, into the bot.
  const wire = `M${alert[0] + 13},${alert[1] - 29} C${alert[0] + 50},${
    alert[1] - 29
  } ${bot.x - 30},${bot.y + 24} ${bot.x},${bot.y + 24}`;

  const scene: Scene = (tl, $) => {
    rise(tl, $("ma"), 0.2, 0, 0.5);
    ride(tl, $("price"), $("head")[0], price, 0.3, 1.5, 0, fAlert);
    const fired = 1.8;
    tl.fromTo(
      $("flag"),
      { opacity: 0, y: 6 },
      { opacity: 1, y: 0, duration: 0.45, ease: "back.out(2.4)" },
      fired
    );
    draw(tl, $("wire"), fired + 0.3, 0.5, "power2.inOut");
    rise(tl, $("bot-head"), fired + 0.7, 4, 0.4);
    $("row*").forEach((el, i) => rise(tl, el, fired + 0.9 + i * 0.2, 4, 0.4));
    ride(tl, $("price"), $("head")[0], price, fired + 1.1, 1.3, fAlert, 1);
    const rest = fired + 2.6;
    const out = rest + 2.6;
    fade(
      tl,
      [
        ...$("price"),
        ...$("head"),
        ...$("ma"),
        ...$("flag"),
        ...$("wire"),
        ...$("bot-head"),
        ...$("row*"),
      ],
      out
    );
    tl.to({}, { duration: 0.5 }, out + 0.5);
    return rest;
  };

  return (
    <Art id={id} w={W} h={H} scene={scene} play={play} fill={fill}>
      <ChartCard id={id} b={chart} />
      <g data-in="0.1">
        <text
          x={chart.x + 12}
          y={chart.y + 22}
          className={`${TXT.ink} ${TXT.num}`}
          style={font(11.5, 600)}
        >
          BTCUSDT
        </text>
        <text
          x={chart.x + 74}
          y={chart.y + 22}
          className={TXT.label}
          style={font(11, 500)}
        >
          1h
        </text>
        {[0.3, 0.65].map((f) => (
          <line
            key={f}
            x1={plot.x}
            x2={plot.x + plot.w}
            y1={plot.y + f * plot.h}
            y2={plot.y + f * plot.h}
            stroke="var(--viz-rule-soft)"
          />
        ))}
      </g>
      <path
        data-k="ma"
        d={linePath(ma)}
        fill="none"
        stroke={tone("gold")}
        strokeWidth={1.6}
        strokeDasharray="4 3"
        strokeLinecap="round"
      />
      <Line k="price" d={linePath(price)} w={2} />
      <Head k="head" x={end[0]} y={end[1]} />
      <g data-k="flag">
        <line
          x1={alert[0]}
          x2={alert[0]}
          y1={alert[1] - 6}
          y2={alert[1] - 16}
          stroke={tone("gold")}
          strokeWidth={1.8}
        />
        <circle
          cx={alert[0]}
          cy={alert[1]}
          r={4.5}
          fill="var(--viz-card)"
          stroke={tone("gold")}
          strokeWidth={2.2}
        />
        <rect
          x={alert[0] - 13}
          y={alert[1] - 42}
          width={26}
          height={26}
          rx={8}
          fill="var(--viz-card)"
        />
        <rect
          x={alert[0] - 13}
          y={alert[1] - 42}
          width={26}
          height={26}
          rx={8}
          fill={soft("gold")}
          stroke={tone("gold")}
          strokeWidth={1.3}
        />
        <BellRing
          x={alert[0] - 7}
          y={alert[1] - 36}
          width={14}
          height={14}
          color={tone("gold")}
          strokeWidth={2}
        />
      </g>
      <path
        data-k="wire"
        d={wire}
        pathLength={1}
        fill="none"
        stroke="var(--viz-accent)"
        strokeWidth={1.8}
        strokeDasharray="1 1"
        strokeDashoffset={0}
        strokeLinecap="round"
      />
      <g data-in="0.15">
        <Card id={id} x={bot.x} y={bot.y} w={bot.w} h={bot.h} r={12} />
      </g>
      <g data-k="bot-head">
        <rect
          x={bot.x + 10}
          y={bot.y + 10}
          width={22}
          height={22}
          rx={7}
          fill={soft("accent")}
        />
        <Webhook
          x={bot.x + 15}
          y={bot.y + 15}
          width={12}
          height={12}
          color={tone("accent")}
          strokeWidth={2}
        />
        <rect
          x={bot.x + 38}
          y={bot.y + 18}
          width={40}
          height={6}
          rx={3}
          fill="var(--viz-ink-soft)"
        />
      </g>
      {[0, 1, 2].map((i) => (
        <g key={i} data-k={`row${i}`}>
          <circle
            cx={bot.x + 18}
            cy={bot.y + 48 + i * 15}
            r={5.5}
            fill={soft("up")}
          />
          <Check
            x={bot.x + 14.5}
            y={bot.y + 44.5 + i * 15}
            width={7}
            height={7}
            color={tone("up")}
            strokeWidth={3.5}
          />
          <rect
            x={bot.x + 30}
            y={bot.y + 45.5 + i * 15}
            width={i === 1 ? 36 : 48}
            height={5}
            rx={2.5}
            fill="var(--viz-ink-soft)"
          />
        </g>
      ))}
      <g data-in="0.25">
        <Chip
          {...cornerChip(chart, longW, true)}
          text={L.long}
          t="up"
          w={longW}
          sm
        />
      </g>
    </Art>
  );
}

/* ------------------------------------------------------------------------ */
/* DCA: buys more on the dip, the average falls, take-profit closes it.     */
/* ------------------------------------------------------------------------ */

export function DcaThumb({ play, fill }: ThumbProps) {
  const L = useArtLabels();
  const id = useArtId("thumb-dca");
  const chart: Box = { x: 10, y: 10, w: 300, h: 140 };
  const band: Box = { x: 26, y: 42, w: 196, h: 96 };
  const BASE = 0.12;
  const SO = [0.34, 0.56, 0.84];
  const SIZE = [1, 1, 2, 3];
  const levels = [BASE, ...SO];
  const AVG = levels.map((_, i) => {
    const n = SIZE.slice(0, i + 1);
    return (
      levels.slice(0, i + 1).reduce((sum, v, j) => sum + v * n[j], 0) /
      n.reduce((a, b) => a + b, 0)
    );
  });
  // Far enough above the average that their tags never touch.
  const TP = AVG.map((v) => v - 0.18);
  const Y = (v: number) => r2(band.y + v * band.h);
  // The price comes down to the first entry, dips through the others, then
  // rebounds to take-profit.
  const full = mapPts(band, [
    [0, 0.02],
    [0.07, BASE],
    [0.38, 0.47],
    [0.7, 0.93],
    [0.84, 0.66],
    [1.01, 0.4],
  ]);
  const tpY = Y(TP[3]);
  const lowI = full.reduce((b, p, i) => (p[1] > full[b][1] ? i : b), 0);
  const endI = full.findIndex((p, i) => i > lowI && p[1] <= tpY);
  const pts: Pt[] =
    endI > 0 ? [...full.slice(0, endI), [full[endI][0], tpY]] : full;
  const fills = levels
    .map((v) => reach(pts, Y(v), "down"))
    .filter((c): c is NonNullable<typeof c> => c !== null);
  const tp = pts[pts.length - 1];
  const x1 = band.x + band.w;
  const tagX = x1 + 10;
  const tagW = chart.x + chart.w - 10 - tagX;
  const usd = (v: number) => Math.round(3560 - v * 420).toLocaleString("en-US");
  const START = 0.4;
  const DRAW = 3.8;

  const scene: Scene = (tl, $) => {
    // A clean start: no average or take-profit until the first buy.
    tl.set([...$("avg"), ...$("tp")], { opacity: 0 }, 0);
    tl.set($("avg"), { y: Y(AVG[0]) }, 0);
    tl.set($("tp"), { y: Y(TP[0]) }, 0);
    say(tl, $("avg-text")[0], usd(AVG[0]), 0);
    say(tl, $("tp-text")[0], usd(TP[0]), 0);
    ride(tl, $("price"), $("head")[0], pts, START, DRAW);
    fills.forEach((c, i) => {
      const t = START + c.f * DRAW;
      pop(tl, $(`fill${i}`), t, 5);
      ring(tl, $(`ring${i}`), t, 5, 16);
      if (i === 0) {
        tl.to(
          [...$("avg"), ...$("tp")],
          { opacity: 1, duration: 0.4 },
          t + 0.1
        );
        return;
      }
      tl.to(
        $("avg"),
        { y: Y(AVG[i]), duration: 0.6, ease: "power2.inOut" },
        t + 0.1
      );
      tl.to(
        $("tp"),
        { y: Y(TP[i]), duration: 0.6, ease: "power2.inOut" },
        t + 0.1
      );
      say(tl, $("avg-text")[0], usd(AVG[i]), t + 0.4);
      say(tl, $("tp-text")[0], usd(TP[i]), t + 0.4);
    });
    const hit = START + DRAW;
    ring(tl, $("burst"), hit, 5, 26);
    rise(tl, $("win"), hit, 6, 0.5);
    const rest = hit + 1;
    const out = rest + 2.8;
    fade(
      tl,
      [
        ...$("price"),
        ...$("head"),
        ...$("fill*"),
        ...$("win"),
        ...$("avg"),
        ...$("tp"),
      ],
      out
    );
    tl.to({}, { duration: 0.5 }, out + 0.5);
    return rest;
  };

  const tag = (text: string, k: string, t: "up" | "accent") => (
    <>
      <rect x={tagX} y={-7.5} width={tagW} height={15} rx={5} fill={soft(t)} />
      <text
        data-k={k}
        x={tagX + tagW / 2}
        y={3.5}
        textAnchor="middle"
        fill={tone(t)}
        className={TXT.num}
        style={font(11, 500)}
      >
        {text}
      </text>
    </>
  );

  return (
    <Art id={id} w={W} h={H} scene={scene} play={play} fill={fill}>
      <ChartCard id={id} b={chart} />
      <g data-in="0.1">
        {levels.map((v) => (
          <line
            key={v}
            x1={band.x}
            x2={x1}
            y1={Y(v)}
            y2={Y(v)}
            stroke="var(--viz-rule)"
            strokeDasharray="3 4"
          />
        ))}
      </g>
      <g data-k="tp" transform={`translate(0 ${Y(TP[3])})`}>
        <line
          x1={band.x}
          x2={x1}
          stroke={tone("up")}
          strokeWidth={1.2}
          strokeDasharray="5 3"
        />
        {tag(usd(TP[3]), "tp-text", "up")}
      </g>
      <g data-k="avg" transform={`translate(0 ${Y(AVG[3])})`}>
        <line x1={band.x} x2={x1} stroke={tone("accent")} strokeWidth={1.4} />
        {tag(usd(AVG[3]), "avg-text", "accent")}
      </g>
      <Line k="price" d={linePath(pts)} w={2} />
      {fills.map((c, i) => (
        <g key={i}>
          <circle
            data-k={`ring${i}`}
            cx={c.x}
            cy={Y(levels[i])}
            r={5}
            fill="none"
            stroke={tone("accent")}
            strokeWidth={1.6}
            opacity={0}
          />
          <circle
            data-k={`fill${i}`}
            cx={c.x}
            cy={Y(levels[i])}
            r={5}
            fill={tone("accent")}
            stroke="var(--viz-card)"
            strokeWidth={1.6}
          />
        </g>
      ))}
      <circle
        data-k="burst"
        cx={tp[0]}
        cy={tp[1]}
        r={5}
        fill="none"
        stroke={tone("up")}
        strokeWidth={1.6}
        opacity={0}
      />
      <g data-k="win">
        {/* In the tag column, above the take-profit tag: clear of every line. */}
        <Chip
          x={tagX + tagW / 2}
          y={tp[1] - 24}
          text="TP ✓"
          t="up"
          w={Math.min(46, tagW)}
          solid
          sm
        />
      </g>
      <Head k="head" x={tp[0]} y={tp[1]} />
      <text
        data-in="0.2"
        x={band.x}
        y={chart.y + 17}
        className={TXT.label}
        style={font(11, 500)}
      >
        {L.averageEntry}
      </text>
    </Art>
  );
}

/* ------------------------------------------------------------------------ */
/* Hedging: a long and a short on the same market, managed together.        */
/* ------------------------------------------------------------------------ */

export function HedgingThumb({ play, fill }: ThumbProps) {
  const L = useArtLabels();
  const id = useArtId("thumb-hedging");
  const chart: Box = { x: 10, y: 10, w: 300, h: 140 };
  const plot: Box = { x: 26, y: 28, w: 170, h: 108 };
  const entryY = plot.y + plot.h * 0.55;
  const pts = mapPts(plot, [
    [0, 0.56],
    [0.35, 0.6],
    [0.64, 0.38],
    [1, 0.16],
  ]);
  const end = pts[pts.length - 1];
  const side = { x: 212, w: 86 };

  const scene: Scene = (tl, $) => {
    rise(tl, $("entry"), 0.2, 0, 0.4);
    ride(tl, $("price"), $("head")[0], pts, 0.5, 3);
    rise(tl, $("long"), 1.4, 6, 0.5);
    rise(tl, $("short"), 1.6, 6, 0.5);
    count(tl, $("long-v")[0], 0, 4.2, (v) => `+${v.toFixed(1)}%`, 1.6, 2.2);
    count(tl, $("short-v")[0], 0, 1.3, (v) => `−${v.toFixed(1)}%`, 1.8, 2.2);
    const rest = 4.2;
    const out = rest + 2.6;
    fade(tl, [...$("price"), ...$("head"), ...$("long"), ...$("short")], out);
    tl.to({}, { duration: 0.5 }, out + 0.5);
    return rest;
  };

  const position = (
    k: string,
    y: number,
    label: string,
    value: string,
    t: "up" | "down"
  ) => (
    <g data-k={k}>
      <rect
        x={side.x}
        y={y}
        width={side.w}
        height={46}
        rx={10}
        fill={soft(t)}
        stroke={tone(t)}
        strokeOpacity={0.3}
      />
      <text x={side.x + 10} y={y + 18} fill={tone(t)} style={font(11.5, 600)}>
        {label}
      </text>
      <text
        data-k={`${k}-v`}
        x={side.x + 10}
        y={y + 36}
        className={`${TXT.ink} ${TXT.num}`}
        style={font(13, 600)}
      >
        {value}
      </text>
    </g>
  );

  return (
    <Art id={id} w={W} h={H} scene={scene} play={play} fill={fill}>
      <ChartCard id={id} b={chart} />
      <line
        data-k="entry"
        x1={plot.x}
        x2={plot.x + plot.w}
        y1={entryY}
        y2={entryY}
        stroke="var(--viz-rule)"
        strokeDasharray="4 4"
      />
      <Line k="price" d={linePath(pts)} w={2} />
      <Head k="head" x={end[0]} y={end[1]} />
      {position("long", 28, L.long, "+4.2%", "up")}
      {position("short", 86, L.short, "−1.3%", "down")}
    </Art>
  );
}

/* ------------------------------------------------------------------------ */
/* Demo account: paper trading with a virtual balance.                      */
/* ------------------------------------------------------------------------ */

const CANDLES: [number, number, number, number][] = [
  [0.5, 0.42, 0.36, 0.56],
  [0.42, 0.58, 0.38, 0.62],
  [0.58, 0.7, 0.52, 0.78],
  [0.7, 0.62, 0.58, 0.76],
  [0.62, 0.48, 0.44, 0.66],
  [0.48, 0.52, 0.42, 0.58],
  [0.52, 0.36, 0.3, 0.56],
  [0.36, 0.24, 0.18, 0.4],
];

export function DemoThumb({ play, fill }: ThumbProps) {
  const L = useArtLabels();
  const id = useArtId("thumb-demo");
  const chart: Box = { x: 10, y: 14, w: 176, h: 132 };
  const plot: Box = { x: 22, y: 40, w: 152, h: 96 };
  const bal: Box = { x: 198, y: 30, w: 112, h: 100 };
  const demoW = Math.max(42, Math.round(L.demo.length * 6.4 + 18));
  const step = plot.w / CANDLES.length;
  const candles = CANDLES.map(([o, c, h, l], i) => ({
    x: r2(plot.x + step * (i + 0.5)),
    o: plot.y + o * plot.h,
    c: plot.y + c * plot.h,
    h: plot.y + h * plot.h,
    l: plot.y + l * plot.h,
  }));
  const dip = candles[2];
  const balLine = mapPts(
    { x: bal.x + 12, y: bal.y + 58, w: bal.w - 24, h: 30 },
    [
      [0, 1],
      [0.4, 0.75],
      [1, 0],
    ]
  );
  const area = `${linePath(balLine)}L${bal.x + bal.w - 12},${bal.y + 88}L${
    bal.x + 12
  },${bal.y + 88}Z`;

  const scene: Scene = (tl, $) => {
    $("candle*").forEach((el, i) => rise(tl, el, 0.2 + i * 0.1, 5, 0.45));
    pop(tl, $("buy"), 0.55, 5);
    draw(tl, $("balance"), 1, 1.1, "power1.inOut");
    rise(tl, $("balance-area"), 1.4, 0, 0.6);
    count(
      tl,
      $("bal-value")[0],
      10000,
      10684,
      (v) => Math.round(v).toLocaleString("en-US"),
      1,
      1.2
    );
    const rest = 2.6;
    const out = rest + 3;
    fade(
      tl,
      [...$("candle*"), ...$("buy"), ...$("balance"), ...$("balance-area")],
      out
    );
    count(
      tl,
      $("bal-value")[0],
      10684,
      10000,
      (v) => Math.round(v).toLocaleString("en-US"),
      out,
      0.5,
      false
    );
    tl.to({}, { duration: 0.5 }, out + 0.5);
    return rest;
  };

  return (
    <Art id={id} w={W} h={H} scene={scene} play={play} fill={fill}>
      <ChartCard id={id} b={chart} />
      <g data-in="0.1">
        <text
          x={chart.x + 12}
          y={chart.y + 22}
          className={`${TXT.ink} ${TXT.num}`}
          style={font(11.5, 600)}
        >
          BTC/USDT
        </text>
        {/* Same 8px inset from the top and the right edge. */}
        <Chip
          {...cornerChip(chart, demoW, true)}
          text={L.demo}
          t="gold"
          w={demoW}
          sm
        />
      </g>
      {candles.map(({ x, o, c, h, l }, i) => {
        const t = c < o ? "up" : "down";
        return (
          <g key={x} data-k={`candle${i}`}>
            <line
              x1={x}
              x2={x}
              y1={h}
              y2={l}
              stroke={tone(t)}
              strokeWidth={1.4}
              strokeLinecap="round"
            />
            <rect
              x={x - 5}
              y={Math.min(o, c)}
              width={10}
              height={Math.max(Math.abs(o - c), 2)}
              rx={2}
              fill={soft(t)}
              stroke={tone(t)}
              strokeWidth={1.4}
            />
          </g>
        );
      })}
      <Marker k="buy" x={dip.x} y={dip.l + 8} t="up" r={4.5} />
      <g data-in="0.2">
        <Card id={id} x={bal.x} y={bal.y} w={bal.w} h={bal.h} r={12} />
        <text
          x={bal.x + 12}
          y={bal.y + 20}
          className={TXT.label}
          style={font(11, 500)}
        >
          {L.balance}
        </text>
        <text
          x={bal.x + bal.w - 12}
          y={bal.y + 20}
          textAnchor="end"
          fill={tone("up")}
          className={TXT.num}
          style={font(11, 500)}
        >
          +6.8%
        </text>
      </g>
      <text
        data-k="bal-value"
        x={bal.x + 12}
        y={bal.y + 44}
        className={`${TXT.ink} ${TXT.num}`}
        style={font(15, 600)}
      >
        10,684
      </text>
      <path data-k="balance-area" d={area} fill={`url(#${id}-area)`} />
      <Line k="balance" d={linePath(balLine)} w={1.8} />
    </Art>
  );
}
